import React, { useEffect, useCallback, useState, useContext } from 'react';
import styled from 'styled-components';
import Analytics from '@com/core/common/utils/analytics';
import { TTrackClick } from '@com/core/common/types/tracking';
import { I18nLink } from '@com/core/common/translations/i18n';
import { getPageForPath, isExternalLink, removeStartingSlash } from '@com/core/common/utils/url';
import { AppContext } from '@com/core/common/contexts/appContext';

// TODO
// 1. Detection of app links to login, account, etc.
// 2. Prop to disable target=_blank

export type TLinkProps = {
    children: JSX.Element | string;
    href: string;
    ariaLabel?: string;
    id?: string;
    analytics?: TTrackClick;
    linkClass?: string;
    external?: boolean;
};

const Link = ({
    analytics,
    ariaLabel,
    children: linkChildren,
    external = false,
    href = '',
    id,
    linkClass,
}: TLinkProps): React.ReactElement => {
    const [path, setPath] = useState<string>('/');

    const { locale } = useContext(AppContext);

    const [page, setPage] = useState<string>('/');
    const [as, setAs] = useState<string>('/');

    useEffect(() => {
        const sanitizedHref = removeStartingSlash(href || '');
        setPath(sanitizedHref);
    }, [href]);

    useEffect(() => {
        (async () => {
            const pageForPath = await getPageForPath(`/${path}`);
            setPage(pageForPath);
            setAs(`/${[locale.seoLocale, path].filter(Boolean).join('/')}`);
        })();
    }, [locale, path]);

    const navigator = useCallback((): void => {
        if (analytics) {
            Analytics.trackClick(locale, {
                platform: 'web',
                ecommerce: {
                    promoClick: {
                        promotions: [analytics],
                    },
                },
            });
        }
    }, [locale, analytics]);

    // for external links that open in a new tab
    if (external || (href && isExternalLink(href))) {
        return (
            <LinkEl
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ariaLabel}
                className={linkClass}
                id={id}>
                {linkChildren}
            </LinkEl>
        );
    }

    return (
        <I18nLink href={{ pathname: page }} as={as} passHref>
            <LinkEl onClick={navigator} className={linkClass} id={id} aria-label={ariaLabel}>
                {linkChildren}
            </LinkEl>
        </I18nLink>
    );
};

const LinkEl = styled.a``;

export default Link;
